import React from 'react'
import { Case, Default, Else, If, Switch, Then, When } from 'react-if'
import type { Stripe } from 'stripe'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { AsP, AsSpan, CaptionSM, TitleMD } from '@/atoms/Text'
import { GuildOffer } from '@/constants/guild'
import { formatPrice } from '@/services/PriceFormatter'
import { formatPromotionPriceDisplay, getPromotionalPrice, ValidatedPromotion } from '@/services/SaleService'
import { useLocale } from '@/utils/LocaleUtil'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useTimeLeft } from '@/utils/useTimeLeft/useTimeLeft'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'

interface Props {
  className?: string
  planInterval: 'annual' | 'monthly'
  disclosePriceInUSD?: boolean
  separateTierPlan?: boolean
  currency?: string
  offer: GuildOffer
  showFullPriceOnly?: boolean
  hideSocialProofText?: boolean
}

export const OmniGuildPricingLabel: React.FC<Props> = ({
  className,
  currency = 'USD',
  planInterval,
  offer,
  showFullPriceOnly,
  hideSocialProofText,
}) => {
  if ((showFullPriceOnly || hideSocialProofText) && planInterval === 'annual') {
    return null
  }

  return (
    <div className={className}>
      <div className="mt-1">
        <OmniPriceDescriptor
          offer={offer}
          currency={currency}
          planInterval={planInterval}
          hideSocialProofText={hideSocialProofText}
        />
      </div>
    </div>
  )
}

type PriceDescriptorProps = Pick<Props, 'planInterval' | 'offer' | 'currency' | 'hideSocialProofText'>

export const OmniPriceDescriptor: React.FC<PriceDescriptorProps> = ({
  currency = 'USD',
  planInterval,
  offer,
  hideSocialProofText,
}) => {
  const { locale } = useLocale()
  const { t } = useTranslate('guild')
  const { sale, experiments } = useGuildSignupContext()
  const promotion = sale?.promotions.find((p) => p.offer.id === offer[planInterval].bimOfferId) as ValidatedPromotion
  const isPromoApplicable = Boolean(promotion)
  const showThousandsJoined = !hideSocialProofText && Boolean(experiments['thousands-joined-premium-text'])

  const promotionMonths = promotion?.promotionCode?.coupon?.duration_in_months
  const promotionalPrice = getPromotionalPrice(promotion)
  const thatsLessThanPricePerMonth = formatPrice(Math.ceil((promotionalPrice as number) / 12), {
    locale,
    currency,
    includeDecimals: false,
  })

  const yearlyPricePerMonth = isPromoApplicable
    ? thatsLessThanPricePerMonth
    : formatPrice(Math.ceil((offer.annual.standardPrice as number) / 12), {
        locale,
        currency,
        includeDecimals: false,
      })

  let savings = 0

  if (isPromoApplicable && promotionalPrice) {
    if (planInterval === 'monthly') {
      const multiplier = getPromotionMonths(promotion.promotionCode)
      savings = (offer.monthly.standardPrice - promotionalPrice) * multiplier
    } else {
      savings = offer.annual.fullPrice - promotionalPrice
    }
  }

  const formattedSavings = formatPrice(savings, {
    locale,
    currency,
    includeDecimals: false,
  })

  const shouldShowSavingsText = Boolean(savings)

  return (
    <If condition={isPromoApplicable}>
      <Then>
        <Switch>
          <Case condition={shouldShowSavingsText}>
            <If condition={promotion?.promotionCode?.coupon?.duration !== 'forever'}>
              <Then>
                <CaptionSM color="core-gray-600" className="mb-3 max-w-[300px] text-balance">
                  <If condition={showThousandsJoined && planInterval === 'annual' && offer.tier === 'premium'}>
                    <Then>
                      <Translate t={t} i18nKey="thousandsJoinedInPastMonth">
                        <strong>Thousands joined</strong> in past month
                      </Translate>
                    </Then>
                    <Else>
                      <If condition={planInterval === 'monthly'}>
                        <Then>
                          <Translate
                            t={t}
                            i18nKey="totalSavingsForMonthlyV1"
                            values={{ formattedSavings, promotionMonths }}
                          >
                            (That&apos;s{' '}
                            <span className="font-bold text-core-oxide">{{ formattedSavings }} savings</span> over your
                            first {{ promotionMonths }} months)
                          </Translate>
                        </Then>
                        <Else>
                          <Translate t={t} i18nKey="yearlySavingsV1" values={{ formattedSavings }}>
                            (That&apos;s{' '}
                            <span className="font-bold text-core-oxide">{{ formattedSavings }} savings</span>)
                          </Translate>
                        </Else>
                      </If>
                    </Else>
                  </If>
                </CaptionSM>
              </Then>
              <Else>
                <CaptionSM color="core-gray-600" className="mb-3 max-w-[140px] text-balance">
                  <If condition={showThousandsJoined && planInterval === 'annual' && offer.tier === 'premium'}>
                    <Then>
                      <Translate t={t} i18nKey="thousandsJoinedInPastMonth">
                        <strong>Thousands joined</strong> in past month
                      </Translate>
                    </Then>
                    <Else>
                      <If condition={planInterval === 'monthly'}>
                        <Then>
                          <Translate
                            t={t}
                            i18nKey="totalSavingsForMonthlyForever"
                            values={{ formattedSavings, promotionMonths }}
                          >
                            (That&apos;s <span className="font-bold">{{ formattedSavings }}/month</span> savings
                            FOREVER)
                          </Translate>
                        </Then>
                        <Else>
                          <Translate t={t} i18nKey="yearlySavingsForever" values={{ formattedSavings }}>
                            (That&apos;s <span className="font-bold">{{ formattedSavings }}</span> savings FOREVER)
                          </Translate>
                        </Else>
                      </If>
                    </Else>
                  </If>
                </CaptionSM>
              </Else>
            </If>
          </Case>
          <Case condition={sale?.endDate}>
            <CountdownToSave endDate={new Date(sale?.endDate as string)} isAlreadyReduced={planInterval === 'annual'} />
          </Case>
        </Switch>
      </Then>
      <Else>
        <CaptionSM color="core-gray-700" className="mb-3">
          <If condition={showThousandsJoined && planInterval === 'annual' && offer.tier === 'premium'}>
            <Then>
              <Translate t={t} i18nKey="thousandsJoinedInPastMonth">
                <strong>Thousands joined</strong> in past month
              </Translate>
            </Then>
            <Else>
              <If condition={planInterval === 'annual'}>
                <Then>
                  <Translate t={t} i18nKey="thatsLikePricePerMonthV1" values={{ yearlyPricePerMonth }}>
                    (That&apos;s like {{ yearlyPricePerMonth }}/month)
                  </Translate>
                </Then>
                <Else>{t('billedMonthly', 'Billed monthly')}</Else>
              </If>
            </Else>
          </If>
        </CaptionSM>
      </Else>
    </If>
  )
}

export const OmniPricePerInterval: React.FC<Props> = ({
  className,
  currency = 'USD',
  planInterval,
  offer,
  disclosePriceInUSD,
  showFullPriceOnly,
}) => {
  const { locale } = useLocale()
  const { t } = useTranslate('guild')
  const { sale } = useGuildSignupContext()
  const offerId = offer[planInterval].bimOfferId
  const promotion = sale?.promotions.find((p) => p.offer.id === offerId) as ValidatedPromotion
  const isPromoApplicable = Boolean(promotion)
  const planPrice = offer[planInterval].standardPrice

  return (
    <>
      <TitleMD as={AsP} weight="bold" className={className}>
        <Switch>
          <Case condition={showFullPriceOnly && planInterval === 'annual'}>
            <span className="text-black">
              {formatPrice(offer.annual.fullPrice, { locale, includeDecimals: false, currency })}
            </span>
          </Case>
          <Case condition={isPromoApplicable}>
            <Switch>
              <Case condition={planInterval === 'annual'}>
                <ExtraSavingsLabel
                  fullPrice={offer.annual.fullPrice}
                  normalPrice={offer.annual.standardPrice}
                  promoPrice={formatPromotionPriceDisplay(promotion, locale)}
                  currency={currency}
                />
              </Case>
              <Default>
                <span className="font-medium text-tertiary-04 line-through decoration-tertiary-04 decoration-2">
                  {formatPrice(planPrice, { locale, includeDecimals: false, currency })}
                </span>
                &nbsp;
                <span className="pl-1 text-black">{formatPromotionPriceDisplay(promotion, locale)}</span>
              </Default>
            </Switch>
          </Case>
          <Case condition={planInterval === 'annual'}>
            <span className="text-tertiary-04 line-through decoration-tertiary-04 decoration-2">
              {formatPrice(offer.annual.fullPrice, {
                locale,
                includeDecimals: false,
                currency,
              })}
            </span>
            &nbsp;
            <span className="text-black">
              {formatPrice(offer.annual.standardPrice, { locale, includeDecimals: false, currency })}
            </span>
          </Case>
          <Default>
            <>{formatPrice(offer.monthly.standardPrice, { locale, includeDecimals: false, currency })}</>
          </Default>
        </Switch>
        <span className="text-sm font-semibold lowercase text-core-gray-700">
          /{planInterval === 'annual' ? t('year', 'Year') : t('month', 'Month')}
        </span>
        <When condition={disclosePriceInUSD}>
          <CaptionSM as={AsSpan} className="inline-block">
            *
          </CaptionSM>
        </When>
      </TitleMD>
    </>
  )
}

interface CountdownToSaveProps {
  endDate: Date
  isAlreadyReduced?: boolean
}
const CountdownToSave: React.FC<CountdownToSaveProps> = ({ endDate, isAlreadyReduced }) => {
  const { t } = useTranslate('join-the-guild')
  const { days, hours, minutes } = useTimeLeft({ targetDate: endDate })
  let display

  if (isAlreadyReduced) {
    if (days > 0) {
      display = t('daysToSaveAlreadyReduced', {
        days,
        count: days,
        defaultValue: '{{days}} day left to save on already reduced price!',
        defaultValue_other: '{{days}} days left to save on already reduced price!',
      })
    } else if (hours > 0) {
      display = t('hoursToSaveAlreadyReduced', {
        hours,
        count: hours,
        defaultValue: '{{hours}} hour left to save on already reduced price!',
        defaultValue_other: '{{hours}} hours left to save on already reduced price!',
      })
    } else if (minutes > 0) {
      display = t('minutesToSaveAlreadyReduced', {
        minutes,
        count: minutes,
        defaultValue: '{{minutes}} minute left to save on already reduced price!',
        defaultValue_other: '{{minutes}} minutes left to save on already reduced price!',
      })
    } else {
      display = t('lastChanceToSaveAlreadyReduced', 'Last chance to save on already reduced price!')
    }
  } else {
    if (days > 0) {
      display = t('daysToSave', {
        days,
        count: days,
        defaultValue: '{{days}} day left to save',
        defaultValue_other: '{{days}} days left to save',
      })
    } else if (hours > 0) {
      display = t('hoursToSave', {
        hours,
        count: hours,
        defaultValue: '{{hours}} hour left to save',
        defaultValue_other: '{{hours}} hours left to save',
      })
    } else if (minutes > 0) {
      display = t('minutesToSave', {
        minutes,
        count: minutes,
        defaultValue: '{{minutes}} minute left to save',
        defaultValue_other: '{{minutes}} minutes left to save',
      })
    } else {
      display = t('lastChanceToSave', 'Last chance to save!')
    }
  }

  return (
    <CaptionSM color="warm-01" className="mb-3" weight="bold">
      {display}
    </CaptionSM>
  )
}

function ExtraSavingsLabel({
  fullPrice,
  normalPrice,
  promoPrice,
  currency = 'USD',
}: {
  fullPrice: number
  normalPrice: number
  promoPrice: string | null
  currency?: string
}) {
  const { locale } = useLocale()

  return (
    <>
      <span className="relative pr-1 font-medium text-tertiary-04">
        <CloseIcon
          size={40}
          color="tertiary-04"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rotate-[12deg]"
        />
        {formatPrice(fullPrice, { locale, includeDecimals: false, currency })}
      </span>
      &nbsp;
      <span className="relative pr-1 font-medium text-tertiary-04">
        <CloseIcon
          size={40}
          color="tertiary-04"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rotate-[12deg]"
        />
        {formatPrice(normalPrice, { locale, includeDecimals: false, currency })}
      </span>
      <span className="inline-block text-black">{promoPrice}</span>
    </>
  )
}

function isPromotionNTimes(promotion: Stripe.PromotionCode) {
  if (!promotion) return false
  return (
    promotion.coupon.duration === 'repeating' &&
    promotion.coupon.duration_in_months &&
    promotion.coupon.duration_in_months > 1
  )
}

function getPromotionMonths(promotion: Stripe.PromotionCode): number {
  if (!promotion) return 0

  return isPromotionNTimes(promotion) ? (promotion?.coupon?.duration_in_months as number) : 1
}
